'use client';

import { c as _c } from "react/compiler-runtime";
import './dialog.scss';
import { type PropsWithChildren, type ReactNode, Suspense } from 'react';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { OmitFromUnion } from '@sola/schema/dotcom/helpers.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { ButtonStyle } from '../../atoms/button-style/button-style.tsx';
import { cn } from '../../utils/helpers.ts';
import type { FormSubmitProps } from '../form/form.tsx';
import { FormSubmit } from '../form/form.tsx';
import type { ModalContentProps, ModalControlledTriggerProps, ModalProps, ModalTriggerProps } from '../modal/modal.tsx';
import { ModalClose, ModalCloseButton, ModalContent, ModalTitle, useModalContext } from '../modal/modal.tsx';
export type DialogProps = ModalProps;

/**
 * Dialog trigger.
 */
export type DialogTriggerProps = ModalTriggerProps;
export type DialogControlledTriggerProps = ModalControlledTriggerProps;

/**
 * Dialog close.
 */
export const DialogClose = ModalClose;

/**
 * Dialog content.
 */
export type DialogContentProps = ModalContentProps & {
  centered?: boolean;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  supportingText?: ReactNode;
  purpose?: 'default' | 'neutral' | 'positive' | 'warning' | 'negative';
  customIcon?: ReactNode;
  icon?: IconNames;
};

/**
 * Primary configurable content/props required all Dialogs.
 */
export const DialogContent: React.FC<DialogContentProps & {
  ref?: React.Ref<HTMLDivElement>;
}> = ({
  title,
  supportingText,
  size = 'md',
  style,
  purpose = 'neutral',
  customIcon,
  icon,
  children,
  className,
  ref,
  ...props
}) => {
  return <ModalContent ref={ref} aria-describedby={undefined} className={cn(`Dialog__content Dialog__content--${size} flex flex-col overflow-y-auto gap-comfy-sm`, {
    [`${className}`]: !!className
  })} style={{
    ...style
  }} {...props}>
      {/* Header */}
      <header className="flex flex-col">
        {/* Icon and close button */}
        <div className={cn('flex items-center justify-end', {
        'justify-between': icon || customIcon
      })}>
          {(icon || customIcon) && <div className={cn(`Dialog__icon
                Dialog__icon--${purpose} flex items-center justify-center rounded-full`)}>
              {customIcon || icon && <ArkIcon size="24" iconName={icon} />}
            </div>}

          <ModalCloseButton className="Dialog__close mr-comfy-xs mt-comfy-xs" name="Dialog" />
        </div>
      </header>
      <div className="overflow-y-auto px-snug-md -mx-snug-md">
        {/* Heading */}
        <div className="pt-comfy-sm mb-comfy-sm">
          <ModalTitle className="Dialog_title heading-sm ">{title}</ModalTitle>
          {supportingText && <span className="block pt-snug-md body-sm body-neutral-mild">
              {supportingText}
            </span>}
        </div>
        {/*
          Contain body suspense (e.g. a lazily-loaded i18n namespace) here so
          it doesn't bubble to the app-root boundary and blank the page. The
          title and close button render above this boundary and stay present
          during the fallback (keeping the Radix dialog title mounted).
         */}
        <div className="pt-comfy-sm md:pt-comfy-lg">
          <Suspense fallback={null}>{children}</Suspense>
        </div>
      </div>
    </ModalContent>;
};

/**
 * Optional slot for additional dialog content. Necessary for overflow-y scrolling
 */
export const DialogSlot: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="overflow-y-auto py-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Footer containing action buttons.
 */
export const DialogActions: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="Dialog__actions sticky bottom-0 left-0 -mx-snug-md px-snug-md pt-comfy-sm md:pt-comfy-lg flex flex-row gap-comfy-xs md:justify-end">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Dialog's primary CTA.
 */
export const DialogActionPrimary: React.FC<OmitFromUnion<FormSubmitProps, 'className' | 'size'> & {
  type?: 'button' | 'submit';
  onClick?: () => void;
}> = t0 => {
  const $ = _c(14);
  let children;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      type: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
  }
  const type = t1 === undefined ? "button" : t1;
  const {
    isBusy
  } = useModalContext();
  if (type === "submit") {
    let t2;
    if ($[4] !== children || $[5] !== isBusy || $[6] !== props) {
      t2 = <FormSubmit fullWidth={false} className="w-full md:w-fit" size="md" isBusy={isBusy} {...props}>{children}</FormSubmit>;
      $[4] = children;
      $[5] = isBusy;
      $[6] = props;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t2;
  if ($[8] !== children) {
    t2 = <button type="button">{children}</button>;
    $[8] = children;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== isBusy || $[11] !== props || $[12] !== t2) {
    t3 = <ButtonStyle className="w-full md:w-fit" size="md" isBusy={isBusy} {...props}>{t2}</ButtonStyle>;
    $[10] = isBusy;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};

/**
 * Optional footer button to close/cancel the dialog.
 */
export const DialogActionClose: React.FC<PropsWithChildren<{
  disabled?: boolean;
}>> = t0 => {
  const $ = _c(5);
  const {
    children,
    disabled
  } = t0;
  const {
    dismissible,
    isBusy
  } = useModalContext();
  const t1 = disabled || isBusy || !dismissible;
  let t2;
  if ($[0] !== children) {
    t2 = <DialogClose>{children}</DialogClose>;
    $[0] = children;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <ButtonStyle className="w-full md:w-fit" size="md" purpose="neutral" variant="secondary" disabled={t1}>{t2}</ButtonStyle>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export { Modal as Dialog, ModalControlledTrigger as DialogControlledTrigger, ModalTrigger as DialogTrigger } from '../modal/modal.tsx';